import { normalizePaywallSource } from "@/lib/analytics/paywall-source";
import { type AttributionData, getAttributionData } from "@/lib/analytics/utm-tracker";
import { getBrowserReturnTo, sanitizeInternalReturnTo } from "@/lib/paywall/recovery";
import { schoolHref } from "@/lib/school-routes";

const COUPON_MAX_CHARS = 128;

export function getBrowserCouponCode(): string | null {
  if (typeof window !== "undefined") {
    const couponFromUrl = new URLSearchParams(window.location.search).get("coupon")?.trim();
    if (couponFromUrl) {
      return couponFromUrl.slice(0, COUPON_MAX_CHARS);
    }
  }

  if (typeof document === "undefined") return null;

  const match = document.cookie.match(/(?:^|;\s*)coupon_code=([^;]+)/);
  if (!match?.[1]) return null;

  try {
    return decodeURIComponent(match[1]).trim().slice(0, COUPON_MAX_CHARS) || null;
  } catch {
    return match[1].trim().slice(0, COUPON_MAX_CHARS) || null;
  }
}

function appendAttributionParams(params: URLSearchParams, attributionData: AttributionData | null) {
  if (!attributionData) return;

  if (attributionData.utm_source) params.set("utm_source", attributionData.utm_source);
  if (attributionData.utm_medium) params.set("utm_medium", attributionData.utm_medium);
  if (attributionData.utm_campaign) params.set("utm_campaign", attributionData.utm_campaign);
  if (attributionData.utm_term) params.set("utm_term", attributionData.utm_term);
  if (attributionData.utm_content) params.set("utm_content", attributionData.utm_content);
  if (attributionData.gclid) params.set("gclid", attributionData.gclid);
}

export function buildTrackedPaywallUrl({
  attributionData = getAttributionData(),
  coupon,
  extraParams,
  mode,
  returnTo = getBrowserReturnTo(),
  source,
}: {
  attributionData?: AttributionData | null;
  coupon?: string | null;
  extraParams?: Record<string, string | null | undefined>;
  mode?: string | null;
  returnTo?: string | null;
  source?: string | null;
} = {}): string {
  const params = new URLSearchParams();
  const resolvedCoupon = coupon || getBrowserCouponCode();

  if (resolvedCoupon) params.set("coupon", resolvedCoupon);
  params.set("source", normalizePaywallSource(source));
  if (mode) params.set("mode", mode);
  params.set("return_to", sanitizeInternalReturnTo(returnTo));

  if (extraParams) {
    Object.entries(extraParams).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
  }

  appendAttributionParams(params, attributionData);

  const query = params.toString();
  return schoolHref(query ? `/paywall?${query}` : "/paywall", returnTo);
}
